import { boxShadow } from '@/theme/foundations/box-shadow';
import { THEME_ATTRIBUTE } from './colorMode.constants';
import {
  DARK_BORDER,
  DARK_SURFACE,
  buildDarkCssVariables,
  hexToChannels,
} from './darkPalette';

const DARK = `html[${THEME_ATTRIBUTE}="dark"]`;

// Rings that mark a selected/highlighted element keep showing in dark.
const KEPT_SHADOWS = new Set(['light-highlight']);

/**
 * The theme's own elevation shadows, switched off in dark mode: they are light
 * grey glows meant for a white page, and on a dark surface they read as a
 * smudge. Chakra flattens nested tokens with `-` (`light.primary` →
 * `--kula-shadows-light-primary`). Chakra's built-in scale (focus `outline`,
 * menu/popover `md`/`lg`) is left alone.
 */
export const buildDarkShadowVariables = (): Record<string, string> => {
  const vars: Record<string, string> = { '--kula-shadows-primary': 'none' };
  Object.entries(boxShadow).forEach(([key, value]) => {
    const names =
      typeof value === 'string'
        ? [key]
        : Object.keys(value).map((child) => `${key}-${child}`);
    names
      .filter((name) => !KEPT_SHADOWS.has(name))
      .forEach((name) => {
        vars[`--kula-shadows-${name}`] = 'none';
      });
  });
  return vars;
};

/** Every selector below is scoped under the dark attribute on `<html>`. */
const scoped = (selector: string) =>
  selector
    .split(',')
    .map((part) => `${DARK} ${part.trim()}`)
    .join(', ');

const surface = (alpha?: number) =>
  alpha === undefined
    ? DARK_SURFACE
    : `rgb(${hexToChannels(DARK_SURFACE)} / ${alpha})`;

/**
 * react-select (v4) is styled in ~200 places through JS `styles` objects with
 * light hex values, which no variable reaches. Every element it renders
 * carries an Emotion class labelled with its part (`css-1a2b3c-control`,
 * `-menu`, `-option`, `-multiValue`…), so dark mode re-skins it here once,
 * GitHub-style: a dark input fill, a bordered dark menu, grey hover and focus
 * rows. `html[…] [class*=…][class*=…]` (0,3,1) out-ranks the Emotion class
 * (0,1,0) without `!important`.
 */
// Exact part match: the label ends the class list or is followed by a space.
// A plain substring would also hit Chakra's `chakra-form-control` (every
// form field wrapper) or `-menuList` for `-menu`.
const rs = (part: string, extra = '') =>
  [`[class$="-${part}"]`, `[class*="-${part} "]`]
    .map((match) => `[class*="css-"]${match}:not(.chakra-form-control)${extra}`)
    .join(', ');

export const buildReactSelectDarkStyles = () => ({
  [scoped(rs('control'))]: {
    backgroundColor: 'var(--kula-colors-input-fill)',
    // !important: some selects set their border colour with !important.
    borderColor: 'var(--kula-colors-secondary-200) !important',
    color: 'var(--kula-colors-secondary-900)',
    boxShadow: 'none',
  },
  [scoped(rs('control', ':hover'))]: {
    borderColor: 'var(--kula-colors-secondary-300) !important',
  },
  [scoped(rs('control', ':focus-within'))]: {
    borderColor: 'var(--kula-colors-primary-500) !important',
  },
  [scoped(`${rs('singleValue')}, ${rs('input', ' input')}`)]: {
    color: 'var(--kula-colors-secondary-900)',
  },
  [scoped(rs('placeholder'))]: {
    color: 'var(--kula-colors-secondary-500)',
  },
  [scoped(rs('menu'))]: {
    backgroundColor: 'var(--kula-colors-white)',
    border: '1px solid var(--kula-colors-secondary-200)',
    color: 'var(--kula-colors-secondary-900)',
  },
  // The menu list, "no options" and loading messages carry no label of
  // their own; they are the menu's direct children.
  [scoped(rs('menu', ' > div'))]: {
    backgroundColor: 'transparent',
  },
  [scoped(rs('option'))]: {
    backgroundColor: 'transparent',
    color: 'var(--kula-colors-secondary-900)',
    // Row rules (kula Select and others draw them with light hex literals).
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },
  // v4 adds state classes only with a `classNamePrefix`; hover covers the rest.
  [scoped(`${rs('option', ':hover')}, [class*="__option--is-focused"]`)]: {
    backgroundColor: 'var(--kula-colors-secondary-50)',
  },
  [scoped('[class*="__option--is-selected"]')]: {
    backgroundColor: 'var(--kula-colors-primary-50)',
    color: 'var(--kula-colors-primary-900)',
  },
  [scoped(rs('multiValue'))]: {
    backgroundColor: 'var(--kula-colors-secondary-100)',
    borderColor: 'var(--kula-colors-secondary-200)',
    color: 'var(--kula-colors-secondary-900)',
  },
  [scoped(rs('multiValue', ' > div'))]: {
    color: 'var(--kula-colors-secondary-900)',
  },
  [scoped(rs('multiValue', ' > div:last-of-type:hover'))]: {
    backgroundColor: 'var(--kula-colors-secondary-200)',
    color: 'var(--kula-colors-secondary-950)',
  },
  [scoped(rs('indicatorSeparator'))]: {
    backgroundColor: 'var(--kula-colors-secondary-200)',
  },
  [scoped(rs('indicatorContainer'))]: {
    color: 'var(--kula-colors-secondary-600)',
  },
  // Group headings ("They rejected us"...) have no label of their own; they
  // are the group's first child, often given a white background.
  [scoped(rs('group', ' > div:first-of-type'))]: {
    backgroundColor: 'transparent !important',
    color: 'var(--kula-colors-secondary-600) !important',
  },
  // Group headings are labelled `group` too (emotion reuses the label), and
  // some selects rule them with light hex literals.
  [scoped(`${rs('group')}, ${rs('group', ' > div:first-of-type')}`)]: {
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },
});

/**
 * Global styles for dark mode, merged into the Chakra theme's
 * `styles.global`. Inert until `<html data-kula-theme="dark">` is set.
 *
 * `html[…]` (0,1,1) out-ranks Chakra's own `:root, [data-theme]` variable
 * block (0,1,0) regardless of insertion order. The second selector covers any
 * element that carries Chakra's `data-theme` attribute, which re-declares the
 * light variables for its subtree.
 */
export const buildDarkModeStyles = () => ({
  [`${DARK}, ${DARK} [data-theme]`]: {
    ...buildDarkCssVariables(),
    ...buildDarkShadowVariables(),
    // The AI gradient button/spinner blend with `multiply`, which on a dark
    // page multiplies them to black. Read as `var(--kula-ai-blend, multiply)`.
    '--kula-ai-blend': 'normal',
    // Translucent loading / disabled overlays: light literals read as
    // `var(--kula-dark-scrim, rgba(246, 247, 247, 0.8))`; dark dims instead.
    '--kula-dark-scrim': 'rgb(20 19 30 / 0.72)',
    // !important: Chakra's ColorModeProvider writes an inline
    // `color-scheme: light` on <html> on every mount.
    colorScheme: 'dark !important',
  },

  // @kula-ai/components and our own Tailwind build use Tailwind's literal
  // `white` for surfaces (55 kula components), which no variable reaches.
  // Upstream fix: kula should use `bg-base-white` (themeable) for surfaces.
  [scoped(
    '.bg-white, .bg-base-white, .hover\\:bg-white:hover, .focus-within\\:bg-white:focus-within, .group:hover .group-hover\\:bg-white'
  )]: {
    backgroundColor: `${surface()} !important`,
  },
  [scoped('.bg-white\\/90, .bg-white\\/85')]: {
    backgroundColor: `${surface(0.9)} !important`,
  },
  [scoped('.bg-white\\/70, .bg-white\\/65')]: {
    backgroundColor: `${surface(0.7)} !important`,
  },

  // Same gap for text: kula's rich-text editor sets Tailwind's literal
  // `text-black` on its content.
  [scoped('.text-black')]: {
    color: 'var(--kula-colors-secondary-950) !important',
  },

  // Third-party surfaces that hard-code white.
  [scoped('.simplebar-scrollbar::before')]: {
    background: '#67637F',
  },
  [scoped('.fc')]: {
    '--fc-page-bg-color': DARK_SURFACE,
    '--fc-neutral-bg-color': 'var(--kula-colors-secondary-50)',
    '--fc-border-color': DARK_BORDER,
    '--fc-today-bg-color': 'var(--kula-colors-primary-25)',
  },
  [scoped('.EZDrawer__container')]: {
    backgroundColor: `${surface()} !important`,
  },

  // Settings side nav: the selected row's grey is a raw hex in index.css.
  [scoped('.settings-side-nav.active .nav')]: {
    background: 'var(--kula-colors-secondary-50)',
  },

  // The light theme's `p a` blue (#0043CE) is unreadable on the dark page.
  [scoped('p a')]: {
    color: 'var(--kula-colors-primary-700)',
  },

  ...buildReactSelectDarkStyles(),

  // Page-title icons (`KModuleHeader`) take a dark-purple stroke from
  // index.css; on the dark page they follow the title text instead.
  [scoped('.primary-900 path')]: {
    stroke: 'var(--kula-colors-secondary-900)',
  },

  // Highcharts writes its colours as SVG attributes and inline styles, so the
  // overrides need !important. Text follows the page, the plot area is
  // transparent and grid lines take the border colour.
  // HTML data labels (a format containing markup, e.g. the pipeline funnel):
  // Highcharts renders them as spans it colours black inline.
  [scoped('.highcharts-data-label span, .highcharts-data-label span *')]: {
    color: 'var(--kula-colors-secondary-700) !important',
  },
  [scoped('.highcharts-background, .highcharts-plot-background')]: {
    fill: 'transparent !important',
  },
  [scoped(
    '.highcharts-axis-labels text, .highcharts-axis-title, .highcharts-legend-item text, .highcharts-data-label text, .highcharts-title, .highcharts-subtitle, .highcharts-label text, .highcharts-stack-labels text'
  )]: {
    fill: 'var(--kula-colors-secondary-700) !important',
    color: 'var(--kula-colors-secondary-700) !important',
  },
  [scoped(
    '.highcharts-grid-line, .highcharts-axis-line, .highcharts-tick, .highcharts-minor-grid-line'
  )]: {
    stroke: 'var(--kula-colors-secondary-100) !important',
  },
  // Columns, slices and markers are outlined in white to separate them on a
  // white page; on the dark page the outline takes the surface colour.
  [scoped('.highcharts-point')]: {
    stroke: 'var(--kula-colors-white) !important',
  },
  // Label text gets a white outline (a stroked copy) for legibility on
  // white; on the dark page it smudges the text, so it takes the surface.
  [scoped('.highcharts-text-outline')]: {
    stroke: 'var(--kula-colors-white) !important',
    fill: 'var(--kula-colors-white) !important',
  },
  [scoped('.highcharts-tooltip-box')]: {
    fill: 'var(--kula-colors-secondary-50) !important',
    stroke: 'var(--kula-colors-secondary-200) !important',
  },
  [scoped('.highcharts-tooltip text, .highcharts-tooltip span')]: {
    fill: 'var(--kula-colors-secondary-900) !important',
    color: 'var(--kula-colors-secondary-900) !important',
  },

  // kula Select (`kula_custom_select` prefix) paints group headings as a
  // #F8F9FC band and rules options with #E9EBF4 literals inside the package.
  // Both need overriding here until the package reads theme variables.
  [scoped('[class*="kula_custom_select__group-heading"]')]: {
    backgroundColor: 'var(--kula-colors-secondary-50) !important',
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },
  [scoped('[class*="kula_custom_select__group-heading"] *')]: {
    color: 'var(--kula-colors-secondary-600) !important',
  },
  [scoped('[class*="kula_custom_select__option"]')]: {
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },

  // Other light literals baked into @kula-ai/components classes.
  [scoped('.border-\\[\\#e9ebf4\\]')]: {
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },
  [scoped('.bg-\\[\\#D7DAEB\\]')]: {
    backgroundColor: 'var(--kula-colors-secondary-200) !important',
  },

  // Skeleton shimmer: Chakra's skeleton reads these two variables (start
  // shown, pulsing toward end). Callers often pass light `startColor` /
  // `endColor` props, so the dark pair wins with !important.
  [scoped('.chakra-skeleton')]: {
    // Chakra's Skeleton keyframes read the unprefixed names; the theme's
    // baseStyle the `kula`-prefixed ones. Set both.
    '--skeleton-start-color': 'var(--kula-colors-secondary-50) !important',
    '--skeleton-end-color': 'var(--kula-colors-secondary-100) !important',
    '--kula-skeleton-start-color': 'var(--kula-colors-secondary-50) !important',
    '--kula-skeleton-end-color': 'var(--kula-colors-secondary-100) !important',
  },

  // react-content-loader shimmer (Circles cards and ~35 other loaders): an
  // SVG gradient `<id>-animated-diff` whose stops carry light colours as
  // attributes; CSS stop-color beats a presentation attribute.
  [scoped('linearGradient[id$="-animated-diff"] stop')]: {
    stopColor: 'var(--kula-colors-secondary-50) !important',
  },
  [scoped('linearGradient[id$="-animated-diff"] stop[offset="50%"]')]: {
    stopColor: 'var(--kula-colors-secondary-100) !important',
  },

  // react-date-range (report date filters): its stylesheet and ours rule the
  // months and the presets column with light literals. Structural borders
  // only; the day range/preview outlines keep their accent colour.
  [scoped(
    '.rdrMonth, .rdrDefinedRangesWrapper, .rdrStaticRange, .rdrDateDisplayWrapper, .rdrMonthAndYearWrapper, .rdrDateRangePickerWrapper'
  )]: {
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },

  // kula Select's floating (portalled) menu: FloatingMenuShell writes the
  // menu surface as an inline white hex from the package palette, on a
  // classless div; its own --rs-menu-max-h is the only stable hook.
  // Upstream gap: the package should read a theme variable.
  [scoped('div[style*="--rs-menu-max-h"]')]: {
    backgroundColor: `${surface()} !important`,
    borderColor: 'var(--kula-colors-secondary-200) !important',
  },

  // Package illustrations with baked-in white paper (e.g. @kula-ai/assets
  // PenFileAsset) can't be recoloured here; wrappers marked data-dark-dim
  // tone them down so they don't glare on the dark surface.
  [scoped('[data-dark-dim] svg, [data-dark-dim] img')]: {
    filter: 'brightness(0.72) saturate(0.9)',
  },

  // Tailwind's stock green/red (not theme-aware) in @kula-ai/components,
  // e.g. the scorecard Overall evaluation: Strong yes / Yes green, No /
  // Strong no red. Same meaning as light mode, as dark tints.
  [scoped('.bg-green-50')]: {
    backgroundColor: 'var(--kula-colors-green-100) !important',
  },
  [scoped('.bg-red-50')]: {
    backgroundColor: 'var(--kula-colors-red-100) !important',
  },
  [scoped('.border-green-300')]: {
    borderColor: 'var(--kula-colors-green-700) !important',
  },
  [scoped('.border-red-300')]: {
    borderColor: 'var(--kula-colors-red-700) !important',
  },
  [scoped('.text-green-600, .text-green-700')]: {
    color: 'var(--kula-colors-green-700) !important',
  },
  [scoped('.text-red-600, .text-red-700')]: {
    color: 'var(--kula-colors-red-700) !important',
  },

  // Lavender AI wash (AI notetaker card): an arbitrary Tailwind rgba that
  // turns muddy light on a dark page; a dark purple tint instead.
  [scoped('[class*="bg-[rgba(240"]')]: {
    backgroundColor: 'var(--kula-colors-purple-50) !important',
  },

  // kula (Radix) tooltip content: a raised grey with light text, as the
  // Chakra tooltip theme does (theme/components/ToolTip.js).
  [scoped('.z-tooltip[data-state]')]: {
    backgroundColor: 'var(--kula-colors-secondary-200) !important',
    color: 'var(--kula-colors-secondary-950) !important',
    border: '1px solid var(--kula-colors-secondary-300)',
  },

  // No elevation shadows in dark mode: surfaces are separated by borders and
  // tone, the way dark UIs (GitHub's included) do it. Many shadows are
  // literals outside the theme tokens, so this covers them all. Keyboard focus
  // rings (box-shadow on :focus-visible) stay, and so does a shadow drawn as
  // a selection underline: mark that element `data-keep-shadow`.
  [scoped('*:not(:focus-visible):not([data-keep-shadow])')]: {
    boxShadow: 'none !important',
  },

  // Native controls (date pickers, scrollbars, autofill) follow the scheme.
  [scoped('input, textarea, select')]: {
    colorScheme: 'dark',
  },
  [scoped('::placeholder')]: {
    color: 'var(--kula-colors-secondary-500)',
  },
});
